import { gql, MutationHookOptions, QueryHookOptions, useMutation, useQuery } from '@apollo/client'
import {
  Mutation,
  MutationBulkUpdateBasketSmartRulesArgs,
  MutationGenerateBasketSmartRulesArgs,
  MutationProcessSmartRuleUploadArgs,
  MutationRefreshSmartBasketArgs,
  MutationRemoveBasketSmartRulesArgs,
  MutationUploadSmartRuleFileArgs,
  Query,
  QueryBasketArgs,
  QuerySearchGmdnNamesArgs,
  QuerySearchGudidBrandsArgs,
  QuerySearchMasterCatalogArgs,
  QuerySearchSubmittedCategoriesArgs,
  QuerySearchUnspscCommodityArgs,
  QuerySearchUnspscTitlesArgs,
  QuerySmartRuleUploadJobsArgs,
} from '../gen/models'
import { BasketFragment } from './baskets'
import { UploadJobFullFragment } from './upload-job'

export const SmartRuleFragment = gql`
  fragment SmartRuleFragment on SmartRule {
    id
    vendor
    vendorItem
    manufacturer
    manufacturerItem
    unspscClassTitle
    unspscCommodityCode
    tsId
    stanId
    gudidId
    gudidBrand
    commodityId
    gmdnName
    gicTypeOneTypeTwo
    submittedCategory
    submittedSubcategory
    createdAt
    updatedAt
    basketId
    customerId
    negative
  }
`

const QUERY_BASKET_SMART_RULES = gql`
  query basketSmartRules($input: IDArgs!) {
    basket(input: $input) {
      basket {
        id
        smartRules {
          ...SmartRuleFragment
        }
      }
    }
  }
  ${SmartRuleFragment}
`

export function useBasketSmartRulesQuery(options: QueryHookOptions<Pick<Query, 'basket'>, QueryBasketArgs>) {
  return useQuery(QUERY_BASKET_SMART_RULES, options)
}

const BULK_UPDATE_BASKET_SMART_RULES = gql`
  mutation bulkUpdateBasketSmartRules($input: BulkUpdateBasketSmartRulesInput!) {
    bulkUpdateBasketSmartRules(input: $input) {
      ...SmartRuleFragment
      gic {
        id
        name
      }
      typeOne {
        id
        name
      }
      type
      typeTwo {
        id
        name
      }
      unspscClassTitleDesc
      unspscCommodityTitle
    }
  }
  ${SmartRuleFragment}
`

export function useBulkUpdateBasketSmartRulesMutation(
  options?: MutationHookOptions<Pick<Mutation, 'bulkUpdateBasketSmartRules'>, MutationBulkUpdateBasketSmartRulesArgs>,
) {
  return useMutation(BULK_UPDATE_BASKET_SMART_RULES, options)
}

const GENERATE_BASKET_SMART_RULES = gql`
  mutation generateBasketSmartRules($input: GenerateBasketSmartRulesInput!) {
    generateBasketSmartRules(input: $input)
  }
`

export function useGenerateSmartBasketRulesMutation(
  options?: MutationHookOptions<Pick<Mutation, 'generateBasketSmartRules'>, MutationGenerateBasketSmartRulesArgs>,
) {
  return useMutation(GENERATE_BASKET_SMART_RULES, options)
}

const REMOVE_BASKET_SMART_RULES = gql`
  mutation removeBasketSmartRules($input: RemoveBasketSmartRulesInput!) {
    removeBasketSmartRules(input: $input)
  }
`

export function useRemoveSmartBasketRulesMutation(
  options?: MutationHookOptions<Pick<Mutation, 'removeBasketSmartRules'>, MutationRemoveBasketSmartRulesArgs>,
) {
  return useMutation(REMOVE_BASKET_SMART_RULES, options)
}

const GMDN_NAMES_SEARCH = gql`
  query searchGmdnNames($input: SearchGmdnNameInput!) {
    searchGmdnNames(input: $input) {
      data
      total
    }
  }
`

export function useSearchGmdnNamesQuery(
  options?: QueryHookOptions<Pick<Query, 'searchGmdnNames'>, QuerySearchGmdnNamesArgs>,
) {
  return useQuery(GMDN_NAMES_SEARCH, options)
}

const GUDID_BRAND_SEARCH = gql`
  query searchGudidBrands($input: SearchGudidBrandInput!) {
    searchGudidBrands(input: $input) {
      data
      total
    }
  }
`

export function useSearchGudidBrandsQuery(
  options?: QueryHookOptions<Pick<Query, 'searchGudidBrands'>, QuerySearchGudidBrandsArgs>,
) {
  return useQuery(GUDID_BRAND_SEARCH, options)
}

const SUBMITTED_CATEGORIES_SEARCH = gql`
  query searchSubmittedCategories($input: SearchSubmittedCategoryInput!) {
    searchSubmittedCategories(input: $input) {
      data
      total
    }
  }
`

export function useSearchSubmittedCategoriesQuery(
  options?: QueryHookOptions<Pick<Query, 'searchSubmittedCategories'>, QuerySearchSubmittedCategoriesArgs>,
) {
  return useQuery(SUBMITTED_CATEGORIES_SEARCH, options)
}

const UNSPSC_TITLE_SEARCH = gql`
  query searchUnspscTitles($input: SearchUnspscInput!) {
    searchUnspscTitles(input: $input) {
      class
      classTitle
    }
  }
`

export function useSearchUnspscQuery(
  options?: QueryHookOptions<Pick<Query, 'searchUnspscTitles'>, QuerySearchUnspscTitlesArgs>,
) {
  return useQuery(UNSPSC_TITLE_SEARCH, options)
}

const MASTER_CATALOG_BY_PART_NUMBER_SEARCH = gql`
  query searchMasterCatalog($input: SearchMasterCatalogByPartNumberInput!) {
    searchMasterCatalog(input: $input) {
      totalHits
      hits {
        id
        normalizedVendor
        normalizedVendorItem
        normalizedManufacturer
        normalizedManufacturerItem
        normalizedDescription
        udiId
        commodityId
        stanId
        tsId
      }
    }
  }
`

export function useSearchMasterCatalogByPartNumberQuery(
  options?: QueryHookOptions<Pick<Query, 'searchMasterCatalog'>, QuerySearchMasterCatalogArgs>,
) {
  return useQuery(MASTER_CATALOG_BY_PART_NUMBER_SEARCH, options)
}

const REFERSH_BASKET_MUTATION = gql`
  mutation refreshSmartBasket($id: String!) {
    refreshSmartBasket(id: $id) {
      ...BasketFragment
    }
  }
  ${BasketFragment}
`

export function useRefreshBasketProducts(
  options?: MutationHookOptions<Pick<Mutation, 'refreshSmartBasket'>, MutationRefreshSmartBasketArgs>,
) {
  return useMutation(REFERSH_BASKET_MUTATION, options)
}

const UNSPSC_COMMODITY_SEARCH = gql`
  query searchUnspscCommodity($input: SearchUnspscInput!) {
    searchUnspscCommodity(input: $input) {
      data {
        commodity
        commodityTitle
      }
      total
    }
  }
`

export function useSearchUnspscCommodityQuery(
  options?: QueryHookOptions<Pick<Query, 'searchUnspscCommodity'>, QuerySearchUnspscCommodityArgs>,
) {
  return useQuery(UNSPSC_COMMODITY_SEARCH, options)
}

const UPLOAD_SMART_RULE_FILE = gql`
  mutation uploadSmartRuleFile($input: UploadBasketInput!) {
    uploadSmartRuleFile(input: $input) {
      s3Key
      data {
        columnName
        previewData
        sheetName
        sheetIndex
      }
    }
  }
`

export const useUploadSmartRuleFile = (
  baseOptions?: MutationHookOptions<Pick<Mutation, 'uploadSmartRuleFile'>, MutationUploadSmartRuleFileArgs>,
) => useMutation(UPLOAD_SMART_RULE_FILE, baseOptions)

const PROCESS_SMART_RULE_UPLOAD = gql`
  mutation processSmartRuleUpload($input: ProcessSmartRuleUploadInput!) {
    processSmartRuleUpload(input: $input)
  }
`

export const useProcessSmartRuleUpload = (
  baseOptions?: MutationHookOptions<Pick<Mutation, 'processSmartRuleUpload'>, MutationProcessSmartRuleUploadArgs>,
) => useMutation(PROCESS_SMART_RULE_UPLOAD, baseOptions)

const SMART_RULE_UPLOAD_JOBS = gql`
  query smartRuleUploadJobs($basketId: String!) {
    smartRuleUploadJobs(basketId: $basketId) {
      ...UploadJobFullFragment
    }
  }
  ${UploadJobFullFragment}
`

export function useSmartRuleUploadJobsQuery(
  options?: QueryHookOptions<Pick<Query, 'smartRuleUploadJobs'>, QuerySmartRuleUploadJobsArgs>,
) {
  return useQuery(SMART_RULE_UPLOAD_JOBS, options)
}
